import type { TextRendererOptions } from '@affine/core/blocksuite/ai/components/text-renderer';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import type { AppThemeService } from '@affine/core/modules/theme';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import type { EditorHost } from '@blocksuite/affine/std';
import { NotificationProvider } from '@blocksuite/affine-shared/services';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import type { DocDisplayConfig } from '../../../components/ai-chat-chips';
import { type ChatMessage } from '../../../components/ai-chat-messages';
import { shouldHideChatMessage } from '../../../components/ai-chat-messages/message-visibility';
import type { AIChatCanvasGenerationState } from '../model';
import { UserInfoTemplate } from './user-info';

export class AIChatBlockMessage extends LitElement {
  static override styles = css`
    .ai-chat-message {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: 4px;
      box-sizing: border-box;
    }

    .ai-chat-user-content {
      width: 100%;
      box-sizing: border-box;
    }

    .ai-chat-generation-error {
      color: var(--streamlain-text-secondary-color);
    }
  `;

  override render() {
    const { userName, userId, avatarUrl, role } = this.message;

    if (role === 'assistant') {
      if (
        this.state === 'error' &&
        !this.message.content &&
        !this.message.streamObjects?.length
      ) {
        return html`<div class="ai-chat-generation-error" role="alert">
          ${I18n.t('com.streamlain.ai.chat.canvas-generation-failed')}
        </div>`;
      }
      return html`<chat-message-assistant
        .host=${this.host}
        .workspaceId=${this.host.store.workspace.id}
        .session=${this.session}
        .item=${this.message}
        .isLast=${this.isLast}
        .status=${this.state === 'generating'
          ? this.message.content || this.message.streamObjects?.length
            ? 'transmitting'
            : 'loading'
          : 'idle'}
        .extensions=${this.textRendererOptions.extensions ?? []}
        .affineFeatureFlagService=${this.textRendererOptions
          .affineFeatureFlagService}
        .affineThemeService=${this.affineThemeService}
        .notificationService=${this.host.std.getOptional(NotificationProvider)}
        .retry=${this.retry}
        .independentMode=${true}
        .docDisplayService=${this.docDisplayService}
        .codeSelections=${[]}
        .peekViewService=${this.peekViewService}
        .onOpenDoc=${this.onOpenDoc}
        .workspaceDocSyncEngine=${undefined}
        .readOnlyPresentation=${true}
      ></chat-message-assistant>`;
    }

    return html`
      <div class="ai-chat-message">
        ${UserInfoTemplate(
          {
            userId: userId ?? undefined,
            userName: userName ?? undefined,
            avatarUrl: avatarUrl ?? undefined,
          },
          role
        )}
        <div class="ai-chat-user-content">
          <chat-message-user .item=${this.message}></chat-message-user>
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor message!: ChatMessage;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor state: AIChatCanvasGenerationState = 'finished';

  @property({ attribute: false })
  accessor isLast = false;

  @property({ attribute: false })
  accessor textRendererOptions: TextRendererOptions = {};

  @property({ attribute: false })
  accessor session: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor affineThemeService: AppThemeService | undefined;

  @property({ attribute: false })
  accessor docDisplayService: DocDisplayConfig | undefined;

  @property({ attribute: false })
  accessor peekViewService: PeekViewService | undefined;

  @property({ attribute: false })
  accessor onOpenDoc: ((docId: string, sessionId?: string) => void) | undefined;

  @property({ attribute: false })
  accessor retry: () => void = () => {};
}

export class AIChatBlockMessages extends LitElement {
  static override styles = css`
    :host {
      width: 100%;
      box-sizing: border-box;
    }

    .ai-chat-messages {
      display: flex;
      box-sizing: border-box;
      width: 100%;
      flex-direction: column;
      gap: 24px;
    }
  `;

  override render() {
    // Keep the last empty assistant turn mounted while it owns meaningful UI.
    // During generation it renders the shared loading indicator; on failure it
    // renders the canvas error state. Hiding it would leave an empty block.
    const keepEmptyLastAssistant =
      this.state === 'generating' || this.state === 'error';
    const visibleMessages = this.messages.filter(
      (message, index) =>
        !shouldHideChatMessage(
          message,
          this.messages,
          index === this.messages.length - 1,
          keepEmptyLastAssistant
        )
    );
    const needsLoadingPlaceholder =
      this.state === 'generating' &&
      visibleMessages.at(-1)?.role !== 'assistant';

    return html`<div class="ai-chat-messages">
      ${repeat(
        visibleMessages,
        message => message.id || message.createdAt,
        (message, index) => {
          return html`
            <ai-chat-block-message
              .host=${this.host}
              .textRendererOptions=${this.textRendererOptions}
              .session=${this.session}
              .affineThemeService=${this.affineThemeService}
              .docDisplayService=${this.docDisplayService}
              .peekViewService=${this.peekViewService}
              .onOpenDoc=${this.onOpenDoc}
              .retry=${this.retry}
              .message=${message}
              .isLast=${index === visibleMessages.length - 1}
              .state=${index === visibleMessages.length - 1
                ? this.state
                : 'finished'}
            ></ai-chat-block-message>
          `;
        }
      )}
      ${needsLoadingPlaceholder ? html`<ai-loading></ai-loading>` : null}
    </div>`;
  }

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor messages: ChatMessage[] = [];

  @property({ attribute: false })
  accessor state: AIChatCanvasGenerationState = 'finished';

  @property({ attribute: false })
  accessor textRendererOptions: TextRendererOptions = {};

  @property({ attribute: false })
  accessor session: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor affineThemeService: AppThemeService | undefined;

  @property({ attribute: false })
  accessor docDisplayService: DocDisplayConfig | undefined;

  @property({ attribute: false })
  accessor peekViewService: PeekViewService | undefined;

  @property({ attribute: false })
  accessor onOpenDoc: ((docId: string, sessionId?: string) => void) | undefined;

  @property({ attribute: false })
  accessor retry: () => void = () => {};
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-chat-block-message': AIChatBlockMessage;
    'ai-chat-block-messages': AIChatBlockMessages;
  }
}
